import * as React from "react";
import { useMotionValueEvent, useScroll } from "motion/react";
import {
  type Chapter,
  DETECTION_OFFSET_PX,
  discoverChapters,
} from "./chapter-progress.lib";

// How far above the detection line (DETECTION_OFFSET_PX) a clicked chapter's
// block-top comes to rest: a larger margin scrolls further, seating the chapter
// higher on the screen. At 60 the block-top lands ~90px from the top — close to
// the top, with the chapter block's own top padding keeping the heading just
// clear of the fixed top bar on mobile. Landing precision no longer drives
// active state (the click pins it, below), so this is purely a resting-position
// choice; a higher rest also widens the manual-scroll buffer before the active
// chapter flips to the previous one.
const SETTLE_MARGIN_PX = 60;

// Matches the `xl:` classes that turn the top bar into the floating panel.
const FLOATING_MEDIA_QUERY = "(min-width: 80rem)";

export type RevealRatio = number | { bar: number; floating: number };

// Settle-watcher tuning. The watcher polls window.scrollY each frame after a
// click-jump and resumes scroll tracking once the page has arrived.
const SETTLE_REACHED_PX = 2; // within this many px of target = arrived
const SETTLE_STABLE_FRAMES = 3; // frames unchanged after moving = stopped short
const SETTLE_MAX_FRAMES = 180; // ~3s hard cap so tracking always resumes

// The window scrollY that lands a chapter's heading on the detection line (less
// the settle margin) — shared by click-to-scroll and deep-link-on-load so both
// land identically. Consumers that deep-link should also set a matching
// `scroll-margin-top` (DETECTION_OFFSET_PX - SETTLE_MARGIN_PX) so the browser's
// native fragment jump agrees and there's no pre-hydration flash.
function chapterScrollTop(el: HTMLElement): number {
  return (
    el.getBoundingClientRect().top +
    window.scrollY -
    DETECTION_OFFSET_PX +
    SETTLE_MARGIN_PX
  );
}

/**
 * Owns all the imperative behaviour for ChapterProgress: discovering chapters,
 * revealing the bar, tracking the active chapter, and scrolling to a chapter on
 * select. The component stays purely declarative.
 *
 * Active tracking reads live element positions against a detection line
 * (DETECTION_OFFSET_PX from the top) on every scroll, resize, and layout
 * reflow rather than from a cached snapshot, so the active chapter stays
 * correct even as lazy media and Rive animations shift the layout.
 *
 * Scroll-driven tracking runs off Motion's `useScroll` MotionValue rather than
 * a raw `scroll` listener: `scrollY` is already batched to Motion's frame loop,
 * so each change fires at most one recompute per frame with no manual rAF
 * coalescing.
 */
export function useChapterProgress(
  detectionRatio?: number,
  revealRatio?: RevealRatio,
) {
  const [chapters, setChapters] = React.useState<Chapter[]>([]);
  const [activeIdx, setActiveIdx] = React.useState<number | null>(null);
  const [visible, setVisible] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const chapterElsRef = React.useRef<HTMLElement[]>([]);
  const idsRef = React.useRef<(string | null)[]>([]);
  const endElRef = React.useRef<HTMLElement | null>(null);

  // While a click-jump is animating we pin the active chapter to the clicked
  // one and suspend scroll-driven recompute, so a not-yet-settled scroll
  // position can't momentarily flip the active chapter to a neighbour. The rAF
  // id lets a re-click (or unmount) cancel an in-flight settle watcher, and the
  // monotonic jump sequence makes any rAF callback from a superseded click a
  // no-op even if it was already scheduled before the cancel landed.
  const trackingSuspendedRef = React.useRef(false);
  const settleRafRef = React.useRef(0);
  const jumpSeqRef = React.useRef(0);

  const { scrollY } = useScroll();

  // Read live positions to decide which chapter is active, so the result is
  // never stale even if the layout has shifted since mount. Reads from refs so
  // it stays referentially stable across renders.
  const recompute = React.useCallback(() => {
    // Pinned during a click-jump — leave activeIdx/visible on the clicked
    // chapter until the scroll has settled and tracking resumes.
    if (trackingSuspendedRef.current) return;
    const chapterEls = chapterElsRef.current;
    if (chapterEls.length === 0) return;
    const line =
      detectionRatio != null
        ? window.innerHeight * detectionRatio
        : DETECTION_OFFSET_PX;
    // Active = the last chapter (document order, sorted by top) whose top has
    // crossed the detection line. Stop at the first one that hasn't.
    let active: number | null = null;
    for (let i = 0; i < chapterEls.length; i++) {
      if (chapterEls[i].getBoundingClientRect().top <= line) active = i;
      else break;
    }
    // Clamp guard: a chapter near the end of the document may not have enough
    // scroll range left to bring its top up to the line — so by the rule above
    // it could never be active, and a click-jump (or a normal scroll) to the
    // last chapter would settle back on the previous one. When the page is
    // clamped at its maximum scroll, fall back to the last chapter that has
    // actually scrolled into view.
    const maxScrollTop = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight,
    );
    if (maxScrollTop > 0 && window.scrollY >= maxScrollTop - 2) {
      const viewportBottom = window.innerHeight;
      for (let i = chapterEls.length - 1; i >= 0; i--) {
        if (chapterEls[i].getBoundingClientRect().top <= viewportBottom) {
          if (active == null || i > active) active = i;
          break;
        }
      }
    }
    // Fade out when the end marker's top reaches the line; without a marker,
    // fall back to hiding once the last chapter's bottom passes it.
    const lastEl = chapterEls[chapterEls.length - 1];
    const endEl = endElRef.current;
    const endReached = endEl
      ? endEl.getBoundingClientRect().top <= line
      : lastEl.getBoundingClientRect().bottom <= line;
    // Lets a page with a low detection line hold the bar back past the hero.
    const ratio =
      typeof revealRatio === "object"
        ? window.matchMedia(FLOATING_MEDIA_QUERY).matches
          ? revealRatio.floating
          : revealRatio.bar
        : revealRatio;
    const revealed =
      ratio == null ||
      chapterEls[0].getBoundingClientRect().top <= window.innerHeight * ratio;
    setActiveIdx(active);
    // Reveal once the first chapter is halfway in (active != null) and hide
    // again past the end.
    setVisible(active != null && revealed && !endReached);
  }, [detectionRatio, revealRatio]);

  // Discovery + layout-shift tracking.
  React.useEffect(() => {
    const { chapterEls, names, ids, analyticsIds, endEl } = discoverChapters();
    chapterElsRef.current = chapterEls;
    idsRef.current = ids;
    endElRef.current = endEl;
    setChapters(
      names.map((name, i) => ({
        name,
        id: ids[i],
        // eslint-disable-next-line analytics/id-must-be-static -- data-chapter-analytics-id is a literal at every chapter element, or the fixed unlabelled fallback
        analyticsId: analyticsIds[i],
      })),
    );
    if (chapterEls.length === 0) return;

    recompute(); // seed synchronously

    // Deep-link on load: if the URL hash matches a chapter id, land on it using
    // the same offset as a click so it reads as active under the fixed header.
    // Runs after a frame so it wins over the browser's native fragment jump
    // (which lands the heading at top:0, under the header) and Next.js scroll
    // restoration; `scroll-margin-top` on the block keeps that native jump in
    // agreement so there's no visible correction.
    const hash = decodeURIComponent(window.location.hash.slice(1));
    const hashIdx = hash ? ids.indexOf(hash) : -1;
    if (hashIdx !== -1) {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: chapterScrollTop(chapterEls[hashIdx]),
          behavior: "auto",
        });
        setActiveIdx(hashIdx);
      });
    }

    // Motion's `useScroll` covers scroll-driven movement (see below). A
    // ResizeObserver on the body catches layout shifts that move chapters
    // *without* a scroll — lazy media, Rive, accordions — by firing whenever
    // content reflows the page height.
    //
    // We can't use an IntersectionObserver here: detecting a chapter crossing
    // the line needs a zero-height root line at DETECTION_OFFSET_PX, but a
    // fixed-px line isn't expressible in rootMargin (it would need
    // calc(150px - 100%) on the bottom). A tall band instead leaves already-
    // intersecting chapters immune to threshold crossings on a shift, so the
    // active chapter would go stale. The ResizeObserver sidesteps the geometry
    // entirely — it reacts to the reflow itself, then recompute() reads live
    // positions against the line.
    window.addEventListener("resize", recompute);
    const resizeObserver = new ResizeObserver(() => recompute());
    resizeObserver.observe(document.body);

    return () => {
      window.removeEventListener("resize", recompute);
      resizeObserver.disconnect();
    };
  }, [recompute]);

  // Cancel any in-flight settle watcher on unmount.
  React.useEffect(() => {
    return () => {
      if (settleRafRef.current) cancelAnimationFrame(settleRafRef.current);
    };
  }, []);

  // Scroll-driven active tracking. `useMotionValueEvent` fires on Motion's
  // frame loop, so this is already coalesced to one recompute per frame.
  useMotionValueEvent(scrollY, "change", recompute);

  // Close the panel on click-outside / Escape.
  React.useEffect(() => {
    if (!expanded) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setExpanded(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [expanded]);

  const scrollToChapter = React.useCallback(
    (idx: number) => {
      const el = chapterElsRef.current[idx];
      if (!el) return;
      // Land the heading on the detection line (DETECTION_OFFSET_PX from the top)
      // so the chapter is active when the scroll settles.
      const top = chapterScrollTop(el);
      setExpanded(false);

      // Make the section shareable/bookmarkable. `replaceState` (not a real
      // navigation) writes the hash without stacking 22 history entries and
      // without triggering its own scroll. No-op for consumers without ids.
      const id = idsRef.current[idx];
      if (id) history.replaceState(history.state, "", `#${id}`);

      // Pin the active chapter to the click and suspend scroll-driven recompute
      // for the duration of the animation. Otherwise a not-yet-settled scroll
      // position (or, on iOS, a stray late scroll event) can flip the active
      // chapter to a neighbour mid-flight — the "nav jumped to the wrong chapter"
      // bug. The watcher below resumes tracking once the page has arrived.
      setActiveIdx(idx);
      trackingSuspendedRef.current = true;
      // Claim this jump. Any rAF callback below checks `seq` against the live
      // ref and bails if a newer click has superseded it, so rapid re-clicks
      // can't run two watchers or scroll to a stale target out of order.
      const seq = ++jumpSeqRef.current;
      if (settleRafRef.current) cancelAnimationFrame(settleRafRef.current);

      // Watch the scroll to completion, then resume tracking. We poll
      // window.scrollY (not scroll events) because iOS Safari does not reliably
      // emit scroll events during a programmatic smooth scroll, but scrollY still
      // updates frame to frame.
      const watchUntilSettled = () => {
        const startY = window.scrollY;
        let lastY = startY;
        let movedYet = false;
        let stableFrames = 0;
        let frames = 0;
        const step = () => {
          if (seq !== jumpSeqRef.current) return; // superseded by a newer click
          const y = window.scrollY;
          if (y !== startY) movedYet = true;
          stableFrames = y === lastY ? stableFrames + 1 : 0;
          lastY = y;
          frames += 1;
          const reached = Math.abs(y - top) <= SETTLE_REACHED_PX;
          // "Stopped short" handles unreachable targets (e.g. the last chapter
          // near the page bottom): the page moved, then stopped before `top`.
          const stoppedShort = movedYet && stableFrames >= SETTLE_STABLE_FRAMES;
          if (reached || stoppedShort || frames >= SETTLE_MAX_FRAMES) {
            settleRafRef.current = 0;
            // Resume tracking and reconcile with the final position. Recompute
            // here rather than waiting for the next scroll event: if the jump was
            // interrupted or hit the frame cap the page may have settled somewhere
            // other than the clicked chapter, and no further scroll event is
            // guaranteed (especially on iOS) — so the highlight would otherwise
            // stay wrong indefinitely. The page has settled by now, so this reads
            // the true resting position (clamp-aware, so a final chapter that
            // can't reach the line still wins), not a mid-flight one.
            trackingSuspendedRef.current = false;
            recompute();
            return;
          }
          settleRafRef.current = requestAnimationFrame(step);
        };
        settleRafRef.current = requestAnimationFrame(step);
      };

      // Closing the panel animates its height, and Motion save/restores window
      // scroll while measuring that height — which cancels a smooth scroll kicked
      // off in the same frame. That restore lands on the frame AFTER the close
      // commit, so a single rAF (which fires on that same frame) still loses the
      // race. Wait a second frame so our scroll runs after Motion's restore.
      // Both kickoff frames route through settleRafRef so a re-click cancels
      // them, and both re-check `seq` so a stale kickoff can't scroll us back.
      settleRafRef.current = requestAnimationFrame(() => {
        if (seq !== jumpSeqRef.current) return;
        settleRafRef.current = requestAnimationFrame(() => {
          if (seq !== jumpSeqRef.current) return;
          window.scrollTo({ top, behavior: "smooth" });
          watchUntilSettled();
        });
      });
    },
    [recompute],
  );

  return {
    chapters: chapters.length ? chapters : null,
    activeIdx,
    activeName: activeIdx != null ? chapters[activeIdx]?.name : undefined,
    visible,
    expanded,
    toggleExpanded: () => setExpanded((open) => !open),
    showLabel: expanded || activeIdx == null,
    containerRef,
    scrollToChapter,
  };
}
